import React, { useState } from "react";
import Wordmark from "./components/Wordmark";
import { PickleballBall } from "./SportIcons";

const C = {
  navy:   "#0f2744",
  navyMd: "#163b68",
  gold:   "#F59E0B",
  goldDk: "#D97706",
  white:  "#ffffff",
  muted:  "rgba(255,255,255,0.6)",
  soft:   "rgba(255,255,255,0.08)",
  border: "rgba(255,255,255,0.14)",
};
const F = {
  heading: "'Rajdhani', sans-serif",
  body:    "'Sora', sans-serif",
};

const PAIN = {
  fi: [
    { bad: "WhatsApp-ryhmä kasvaa sekaiseksi", good: "Automaattiset sähköpostikutsut PIN-koodilla" },
    { bad: "Otteluparit tehdään käsin excelissä", good: "Americano-parit generoituvat yhdellä napilla" },
    { bad: "Pistetaulukko kirjoitetaan paperille", good: "Reaaliaikainen tulostaulu kaikille pelaajille" },
  ],
  en: [
    { bad: "WhatsApp groups getting out of hand", good: "Automated email invites with PIN codes" },
    { bad: "Brackets made manually in a spreadsheet", good: "Americano rounds generated in one tap" },
    { bad: "Scores written on paper", good: "Real-time leaderboard for all players" },
  ],
};

const STEPS = {
  fi: [
    { n: "1", title: "Luo klubi", desc: "Rekisteröi klubisi 2 minuutissa. Saat oman klubikoodin ja admin-tunnukset." },
    { n: "2", title: "Kutsu pelaajat", desc: "Lähetä sähköpostikutsut suoraan sovelluksesta. Pelaajat kirjautuvat PIN-koodilla." },
    { n: "3", title: "Pelaa & seuraa", desc: "Generoi otteluparit automaattisesti. Kirjaa tulokset ja seuraa kehitystä." },
  ],
  en: [
    { n: "1", title: "Create your club", desc: "Register your club in 2 minutes. Get your own club code and admin credentials." },
    { n: "2", title: "Invite players", desc: "Send email invites directly from the app. Players join with a PIN code." },
    { n: "3", title: "Play & track", desc: "Auto-generate match rounds. Enter results and track player progress." },
  ],
};

const FEATURES = {
  fi: [
    { icon: "pickleball", title: "Americano ja round robin", desc: "Kierrokset ja parit muodostuvat automaattisesti — vaihtuvin tai kiintein parein." },
    { icon: "🏆", title: "Liiga", desc: "Viikoittaiset illat kerryttävät kauden taulukkoa. Parhaat illat lasketaan, poissaolo ei pudota pelistä, ja jokainen näkee oman sijoituksensa." },
    { icon: "📊", title: "Reaaliaikaiset tulokset", desc: "Pelaajat näkevät tilanteen omalla puhelimellaan heti." },
    { icon: "📈", title: "Pelaajan historia", desc: "DUPR-luokitus kehittyy tapahtumasta toiseen." },
    { icon: "🔐", title: "Sormenjälkikirjautuminen", desc: "Pelaajat kirjautuvat seuraavilla kerroilla biometrialla — ei salasanoja." },
    { icon: "💳", title: "MobilePay-integraatio", desc: "Pelipanokset ja salivuoromaksut suoraan appista." },
    { icon: "📱", title: "PWA — ei asennusta", desc: "Toimii suoraan selaimessa. Asennettavissa kotiruudulle kuten natiivi-app." },
  ],
  en: [
    { icon: "pickleball", title: "Americano & round robin", desc: "Rounds and match-ups generated automatically — rotating or fixed pairs." },
    { icon: "🏆", title: "League", desc: "Weekly nights build a season table. Best nights count, missing one doesn't drop you out, and everyone sees their own rank." },
    { icon: "📊", title: "Real-time Results", desc: "Players see live standings on their own phone." },
    { icon: "📈", title: "Player History", desc: "DUPR rating evolves across events." },
    { icon: "🔐", title: "Biometric Sign-in", desc: "Players return with fingerprint or Face ID — no passwords." },
    { icon: "💳", title: "Payment Integration", desc: "Entry fees and court booking payments built in." },
    { icon: "📱", title: "PWA — no install needed", desc: "Works in the browser. Installable to home screen like a native app." },
  ],
};

export default function ClubsLandingPage() {
  const [lang, setLang] = useState<"fi"|"en">(() =>
    (localStorage.getItem("spinloop_lang") as "fi"|"en") || "en"
  );
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [clubName, setClubName] = useState("");

  function toggleLang() {
    const next = lang === "fi" ? "en" : "fi";
    setLang(next);
    localStorage.setItem("spinloop_lang", next);
  }

  function handleContact(e: React.FormEvent) {
    e.preventDefault();
    // mailto fallback — voidaan korvata EmailJS:llä myöhemmin
    window.location.href = `mailto:info@spinloop.app?subject=Club signup: ${clubName}&body=Club: ${clubName}%0AEmail: ${email}`;
    setEmailSent(true);
  }

  const pain     = PAIN[lang];
  const steps    = STEPS[lang];
  const features = FEATURES[lang];

  const T = lang === "fi" ? {
    nav_cta:     "Avaa sovellus",
    hero_h1a:    "Lopeta turnausten pyörittäminen",
    hero_h1b:    "WhatsAppissa ja Excelissä.",
    hero_sub:    "Spinloop antaa klubillesi ammattimaisen turnaushallinnan — ilmaiseksi.",
    hero_cta:    "Aloita ilmaiseksi →",
    hero_sub2:   "Ei luottokorttia · Ei asennusta · Toimii heti",
    pain_title:  "Tunnistetko nämä?",
    steps_title: "Näin se toimii",
    feat_title:  "Kaikki mitä tarvitset",
    price_title: "Hinnoittelu",
    price_free:  "Ilmainen",
    price_desc:  "Kaikki ominaisuudet käytössä. Ei piilokuluja.",
    price_inc:   ["Rajaton määrä tapahtumia", "Rajaton määrä pelaajia", "Sähköpostikutsut", "Reaaliaikaiset tulokset", "Pelaajan historia & luokitukset", "MobilePay-integraatio"],
    price_cta:   "Luo klubi nyt →",
    contact_title: "Haluatko demon tai lisätietoa?",
    contact_sub:   "Lähetä viesti — otamme yhteyttä 24h sisällä.",
    contact_club:  "Klubin nimi",
    contact_email: "Sähköpostisi",
    contact_btn:   "Lähetä →",
    contact_sent:  "Kiitos! Otamme yhteyttä pian.",
    footer:        "Tehty pickleballin rakastajille · spinloop.app",
  } : {
    nav_cta:     "Open app",
    hero_h1a:    "Stop running tournaments",
    hero_h1b:    "in WhatsApp and spreadsheets.",
    hero_sub:    "Spinloop gives your Pickleball club professional tournament management — for free.",
    hero_cta:    "Get started free →",
    hero_sub2:   "No credit card · No install · Works instantly",
    pain_title:  "Sound familiar?",
    steps_title: "How it works",
    feat_title:  "Everything you need",
    price_title: "Pricing",
    price_free:  "Free",
    price_desc:  "All features included. No hidden costs.",
    price_inc:   ["Unlimited events", "Unlimited players", "Email invites", "Real-time results", "Player history & ratings", "Payment integration"],
    price_cta:   "Create your club now →",
    contact_title: "Want a demo or more info?",
    contact_sub:   "Send us a message — we'll get back to you within 24h.",
    contact_club:  "Club name",
    contact_email: "Your email",
    contact_btn:   "Send →",
    contact_sent:  "Thanks! We'll be in touch soon.",
    footer:        "Built for pickleball lovers · spinloop.app",
  };

  return (
    <div style={{ fontFamily: F.body, color: C.white, overflowX: "hidden" }}>

      {/* NAV */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(15,39,68,0.96)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${C.border}`,
        padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: F.heading, letterSpacing: 2 }}>
          <Wordmark />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={toggleLang} style={{
            background: "rgba(255,255,255,0.1)", border: `1px solid ${C.border}`,
            borderRadius: 12, padding: "5px 12px", color: C.white,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>
            {lang === "fi" ? "🌐 EN" : "🌐 FI"}
          </button>
          <a href="/" style={{
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldDk})`,
            color: C.navy, borderRadius: 12, padding: "8px 18px",
            fontSize: 13, fontWeight: 700, textDecoration: "none", fontFamily: F.heading,
          }}>
            {T.nav_cta}
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        backgroundImage: "linear-gradient(160deg, rgba(10,25,50,0.97) 0%, rgba(22,59,104,0.95) 100%), url('/court-bg.jpg')",
        backgroundSize: "cover", backgroundPosition: "center",
        padding: "72px 24px 80px", textAlign: "center",
      }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <div style={{
            display: "inline-block", background: "rgba(245,158,11,0.15)",
            border: "1px solid rgba(245,158,11,0.35)",
            borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
            color: C.gold, letterSpacing: "0.05em", marginBottom: 24,
            textTransform: "uppercase",
          }}>
            <PickleballBall size={14} /> Pickleball
          </div>

          <h1 style={{
            fontSize: 44, fontWeight: 700, fontFamily: F.heading,
            lineHeight: 1.1, letterSpacing: 1, marginBottom: 20,
          }}>
            {T.hero_h1a}<br />
            <span style={{ color: C.gold }}>{T.hero_h1b}</span>
          </h1>

          <p style={{ fontSize: 16, color: C.muted, marginBottom: 36, lineHeight: 1.7, maxWidth: 420, margin: "0 auto 36px" }}>
            {T.hero_sub}
          </p>

          <a href="/" style={{
            display: "inline-block",
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldDk})`,
            color: C.navy, borderRadius: 16, padding: "16px 40px",
            fontSize: 18, fontWeight: 700, fontFamily: F.heading,
            textDecoration: "none", letterSpacing: 0.5,
            boxShadow: "0 8px 32px rgba(245,158,11,0.35)",
          }}>
            {T.hero_cta}
          </a>
          <div style={{ marginTop: 14, fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
            {T.hero_sub2}
          </div>
        </div>
      </section>

      {/* PAIN → SOLUTION */}
      <section style={{ background: "#0a1e3d", padding: "64px 24px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, fontFamily: F.heading, textAlign: "center", marginBottom: 36, letterSpacing: 1 }}>
            {T.pain_title}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {pain.map((p, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center", gap: 12,
                background: C.soft, borderRadius: 14,
                padding: "16px 18px", border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 13, color: "rgba(255,100,100,0.85)", lineHeight: 1.4 }}>
                  ❌ {p.bad}
                </div>
                <div style={{ fontSize: 18, color: C.gold }}>→</div>
                <div style={{ fontSize: 13, color: "rgba(100,220,130,0.9)", lineHeight: 1.4 }}>
                  ✅ {p.good}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ background: C.navyMd, padding: "64px 24px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, fontFamily: F.heading, textAlign: "center", marginBottom: 40, letterSpacing: 1 }}>
            {T.steps_title}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                  background: `linear-gradient(135deg, ${C.gold}, ${C.goldDk})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18, fontWeight: 900, color: C.navy, fontFamily: F.heading,
                }}>
                  {s.n}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, fontFamily: F.heading, letterSpacing: 0.5 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ background: "#0a1e3d", padding: "64px 24px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, fontFamily: F.heading, textAlign: "center", marginBottom: 36, letterSpacing: 1 }}>
            {T.feat_title}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {features.map((f, i) => (
              <div key={i} style={{
                background: C.soft, borderRadius: 16,
                padding: "18px 14px", border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 26, marginBottom: 8 }}>{f.icon === "pickleball" ? <PickleballBall size={30} /> : f.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, fontFamily: F.heading, letterSpacing: 0.3 }}>{f.title}</div>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section style={{ background: C.navyMd, padding: "64px 24px" }}>
        <div style={{ maxWidth: 400, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, fontFamily: F.heading, marginBottom: 32, letterSpacing: 1 }}>
            {T.price_title}
          </h2>
          <div style={{
            background: "rgba(245,158,11,0.08)", border: `2px solid ${C.gold}`,
            borderRadius: 24, padding: "32px 28px",
          }}>
            <div style={{ fontSize: 40, fontWeight: 900, fontFamily: F.heading, color: C.gold, marginBottom: 4 }}>
              {T.price_free}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>{T.price_desc}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28, textAlign: "left" }}>
              {T.price_inc.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                  <span style={{ color: C.gold, fontSize: 16 }}>✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <a href="/" style={{
              display: "block",
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldDk})`,
              color: C.navy, borderRadius: 14, padding: "14px 0",
              fontSize: 15, fontWeight: 700, fontFamily: F.heading,
              textDecoration: "none", letterSpacing: 0.5,
            }}>
              {T.price_cta}
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT / DEMO */}
      <section style={{ background: "#0a1e3d", padding: "64px 24px" }}>
        <div style={{ maxWidth: 440, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, fontFamily: F.heading, marginBottom: 8, letterSpacing: 1 }}>
            {T.contact_title}
          </h2>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 28 }}>{T.contact_sub}</p>

          {emailSent ? (
            <div style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: "20px", fontSize: 15, color: "#4ade80" }}>
              ✅ {T.contact_sent}
            </div>
          ) : (
            <form onSubmit={handleContact} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input
                value={clubName} onChange={e => setClubName(e.target.value)}
                placeholder={T.contact_club} required
                style={{ padding: "13px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.soft, color: C.white, fontSize: 14, outline: "none", fontFamily: F.body }}
              />
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder={T.contact_email} required
                style={{ padding: "13px 16px", borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.soft, color: C.white, fontSize: 14, outline: "none", fontFamily: F.body }}
              />
              <button type="submit" style={{
                background: `linear-gradient(135deg, ${C.gold}, ${C.goldDk})`,
                color: C.navy, border: "none", borderRadius: 12, padding: "14px",
                fontSize: 15, fontWeight: 700, fontFamily: F.heading, cursor: "pointer",
              }}>
                {T.contact_btn}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: C.navy, padding: "24px", textAlign: "center", borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: F.heading, letterSpacing: 2, marginBottom: 8 }}>
          <Wordmark />
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>{T.footer}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>
          DeepEnd Solutions Oy
          <span style={{ margin: "0 8px" }}>·</span>
          info@spinloop.app
          <span style={{ margin: "0 8px" }}>·</span>
          <a href="/privacy" style={{ color: "rgba(255,255,255,0.3)", textDecoration: "underline" }}>
            {lang === "fi" ? "Tietosuoja" : "Privacy Policy"}
          </a>
        </div>
      </footer>
    </div>
  );
}
